import React, { useState } from 'react';
import { CheckCircle, XCircle, X, AlertCircle, Info } from 'lucide-react';

interface ApprovalModalProps {
  isOpen: boolean;
  hotelName: string;
  type: 'approve' | 'reject';
  onConfirm: (reason?: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  isOpen,
  hotelName,
  type,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (type === 'reject' && !reason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setError('');
      await onConfirm(reason);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const isApprove = type === 'approve';
  const title = isApprove ? 'Approve Listing' : 'Reject Listing';
  const description = isApprove
    ? 'This listing will be approved and visible to guests immediately.'
    : 'This listing will be rejected. The owner will be notified.';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-sage-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-4 p-6 pb-0">
          <div className={`p-2.5 rounded-xl flex-shrink-0 ${isApprove ? 'bg-green-50' : 'bg-red-50'}`}>
            {isApprove ? (
              <CheckCircle size={20} className="text-green-600" />
            ) : (
              <XCircle size={20} className="text-red-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-sage-900">{title}</h2>
            <p className="text-sm text-sage-500 mt-0.5 truncate">{hotelName}</p>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-sage-400 hover:text-sage-600 disabled:opacity-50 p-1 rounded-lg hover:bg-beige-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-sage-600 leading-relaxed">{description}</p>

          {type === 'reject' && (
            <div>
              <label className="block text-sm font-medium text-sage-700 mb-1.5">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                placeholder="Explain why this listing is being rejected..."
                className="w-full px-3.5 py-2.5 border border-beige-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 resize-none text-sm text-sage-900 placeholder-sage-400 transition-colors"
                rows={4}
                disabled={isLoading}
              />
              <p className="text-xs text-sage-400 mt-1.5">
                {reason.length}/500 characters
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {type === 'approve' && (
            <div className="flex items-start gap-2.5 p-3 bg-brand-50 border border-brand-200 rounded-xl">
              <Info size={14} className="text-brand-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-brand-600">
                The owner will be notified of the approval immediately.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-beige-200 px-6 py-4 flex items-center gap-3 bg-beige-50/50">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 border border-beige-300 text-sage-700 rounded-xl hover:bg-beige-100 transition-colors disabled:opacity-50 font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || (type === 'reject' && !reason.trim())}
            className={`flex-1 px-4 py-2.5 text-white rounded-xl transition-colors disabled:opacity-50 font-semibold text-sm ${
              isApprove
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </div>
            ) : isApprove ? (
              'Approve Listing'
            ) : (
              'Reject Listing'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;
